import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { letterFor } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/optionLetters';
import { DailyQuestionOption } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { QuestionMarkdown, markdownContentSx } from './QuestionMarkdown';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.feedback;

type QuizFeedbackCardProps = Readonly<{
  correct: boolean;
  /** The question's options in presented order — letters come from position, never from stored text. */
  options: readonly DailyQuestionOption[];
  /** The question's overall note, or null when the per-option reasons are the whole explanation. */
  explanation: string | null;
}>;

/**
 * Per-question answer feedback: the correct/incorrect state, every option with its own reason, then
 * the question's overall note.
 */
export const QuizFeedbackCard = ({ correct, options, explanation }: QuizFeedbackCardProps) => {
  // Without any per-option rationale, the option column and its heading are empty chrome.
  const hasOptionDetail = options.some((option) => option.explanation);

  return (
    <Box sx={correct ? correctCardSx : incorrectCardSx} id="booster-quiz-feedback">
      <Box sx={headerRowSx}>
        <Box sx={correct ? correctIconSx : incorrectIconSx}>
          <AppIcon
            iconName={correct ? 'ThumbsUp' : 'ThumbsDown'}
            diameter="24px"
            color={
              correct
                ? 'Colors/Foreground/fg-success-primary'
                : 'Colors/Foreground/fg-error-primary'
            }
          />
        </Box>
        <Typography variant="text-lg-semibold" sx={titleSx}>
          {correct ? STRINGS.correct : STRINGS.incorrect}
        </Typography>
      </Box>

      {hasOptionDetail && (
        <Box sx={optionsColSx} id="booster-quiz-feedback-options">
          {options.map((option, index) => (
            <Box key={option.id} sx={optionRowSx(option.isCorrect)}>
              <Typography
                variant="text-xs-semibold"
                component="span"
                sx={letterBadgeSx(option.isCorrect)}
              >
                {letterFor(index)}
              </Typography>
              {option.explanation && (
                <Typography variant="text-sm-regular" component="div" sx={optionReasonSx}>
                  <QuestionMarkdown content={option.explanation} size="SMALL" />
                </Typography>
              )}
              {option.isCorrect && (
                <Box sx={optionMarkSx} role="img" aria-label={STRINGS.correctOptionAria}>
                  <AppIcon
                    iconName="Check"
                    diameter="16px"
                    color="Colors/Foreground/fg-success-primary"
                    strokeWidth="bold"
                  />
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {explanation && (
        <Box sx={overallColSx}>
          {hasOptionDetail && (
            <Typography variant="text-sm-semibold" sx={sectionTitleSx}>
              {STRINGS.overallTitle}
            </Typography>
          )}
          <Typography variant="text-sm-regular" component="div" sx={explanationSx}>
            <QuestionMarkdown content={explanation} size="SMALL" />
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const baseCardSx = (backrs: Theme['backrs'], border: string): object => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  padding: backrs.spacing['spacing-3xl'],
  border: `1px solid ${border}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xs'],
});

const correctCardSx: SxProps<Theme> = ({ backrs }) =>
  baseCardSx(backrs, backrs.colors['Colors/Foreground/fg-success-secondary']);

const incorrectCardSx: SxProps<Theme> = ({ backrs }) =>
  baseCardSx(backrs, backrs.colors['Colors/Border/border-error']);

const headerRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const iconBaseSx = (backrs: Theme['backrs'], bg: string): object => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '48px',
  height: '48px',
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: bg,
});

const correctIconSx: SxProps<Theme> = ({ backrs }) =>
  iconBaseSx(backrs, backrs.colors['Colors/Background/bg-success-secondary']);

const incorrectIconSx: SxProps<Theme> = ({ backrs }) =>
  iconBaseSx(backrs, backrs.colors['Colors/Background/bg-error-secondary']);

const optionsColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const overallColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const optionRowSx =
  (isCorrect: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-md'],
    padding: backrs.spacing['spacing-lg'],
    border: `1px solid ${
      isCorrect
        ? backrs.colors['Colors/Foreground/fg-success-secondary']
        : backrs.colors['Colors/Border/border-secondary']
    }`,
    borderRadius: backrs.radius['radius-md'],
    backgroundColor: isCorrect
      ? backrs.colors['Colors/Background/bg-success-primary']
      : backrs.colors['Colors/Background/bg-primary'],
  });

const letterBadgeSx =
  (isCorrect: boolean): SxProps<Theme> =>
  ({ backrs, typography }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // Centers the badge on the reason's first line.
    width: typography['text-sm-regular']?.lineHeight,
    height: typography['text-sm-regular']?.lineHeight,
    borderRadius: backrs.radius['radius-xs'],
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    backgroundColor: isCorrect
      ? backrs.colors['Colors/Foreground/fg-success-primary']
      : backrs.colors['Colors/Base/White'],
    color: isCorrect
      ? backrs.colors['Colors/Text/text-white']
      : backrs.colors['Colors/Text/text-secondary'],
  });

const optionMarkSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const optionReasonSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  flex: 1,
  minWidth: 0,
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const explanationSx: SxProps<Theme> = ({ backrs }) => ({
  ...markdownContentSx,
  color: backrs.colors['Colors/Text/text-tertiary'],
});
